import React, { useEffect, useRef, useState } from "react";
import { Header3, muiInputFormsCSS, SolidButton, styled } from "@iventis/styles";
import { useResizeObserver } from "@iventis/utilities";
import { GeoJSONFeature } from "@iventis/mapbox-gl";
import type { Feature, Polygon } from "geojson";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { CursorTooltip } from "@iventis/components";
import { CadUploadConfigControlPoint } from "@iventis/domain-model/model/cadUploadConfigControlPoint";
import { Asset } from "@iventis/domain-model/model/asset";
import { useSubjectContext } from "../../state/subjects-context";
import { LayerListComponent } from "../layer-filtering/filtered-layer-list";
import { BackgroundSelectorComponent } from "../map-options/background-selector";
import { OSKey } from "../../api-keys";
import { ClippingModeControls } from "./clipping-mode-controls";
import { GeoReferencerCadMode } from "../../types/georeferencer-cad-mode";
import { ModeStepperComponent } from "../mode-stepper";
import "@iventis/mapbox-gl/dist/mapbox-gl.css";
import { MapboxCadGeoEditor } from "../../mapbox/mapbox-cad-geo-editor";
import { CadSourceGeoReferenced } from "../../geo-helpers/cad-source-geo-referenced";
import { CadConfig } from "../../types/cad-config";
import { CadStyle } from "../../types/style";
import { PerspectiveTransform } from "../../geo-helpers/perspective-transform";
import { useCadApiContext } from "../../context/cad-api-context";

const georeferencedModes = [
    GeoReferencerCadMode.WaitingForFile,
    GeoReferencerCadMode.ClippingAndLayerExclusions,
    GeoReferencerCadMode.LayerExclusions,
    GeoReferencerCadMode.Perimeter,
];

export function CadGeoEditor({
    style,
    sourceEpsgCode,
    controlPoints,
    onFinish,
    getGoogleMapBackgroundApiKey,
    getMapBackgrounds = () => Promise.resolve([]),
}: {
    style: CadStyle;
    sourceEpsgCode?: number;
    controlPoints?: CadUploadConfigControlPoint[];
    onFinish: (cadConfig: CadConfig) => void;
    getGoogleMapBackgroundApiKey: () => Promise<{ apiKey: string; session: string }>;
    getMapBackgrounds?: () => Promise<Asset[]>;
}) {
    const effectiveSourceEpsgCode = sourceEpsgCode ?? 4326;
    const subjects = useSubjectContext();
    const [mode, setMode] = useState<GeoReferencerCadMode>(subjects.mode$.value);
    const [perimeter, setPerimeter] = useState<Polygon>();
    const [clippedArea, setClippedArea] = useState<Polygon>();
    const [clippedArea4326, setClippedArea4326] = useState<Polygon>();
    const [bounding, setBounding] = useState<Polygon>();
    const [excludedLayerIds, setExcludedLayerIds] = useState<string[]>([]);
    const [projecting, setProjecting] = useState<boolean>(false);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [layerNames, setLayerNames] = useState<string[]>([]);
    const [featuresRead, setFeaturesRead] = useState<number>(0);
    const backgroundMapElementRef = useRef<HTMLDivElement>(null);
    const mapboxCadGeoReferencerRef = useRef<MapboxCadGeoEditor>();
    const cadSourceRef = useRef<CadSourceGeoReferenced | null>(null);
    const cadApiContext = useCadApiContext();
    const resizeMap = () => {
        mapboxCadGeoReferencerRef.current?.resizeMap();
    };

    useResizeObserver(backgroundMapElementRef, resizeMap);

    const getCadConfig = () => {
        const config: CadConfig = {
            crop: clippedArea.coordinates[0].map((coord) => [coord[0], coord[1]]),
            excludedLayerIds,
            sourceEpsgCode: controlPoints != null ? null : sourceEpsgCode, // If control points are provided, we do not use the source EPSG code
            controlPoints, // Georeferenced cads may have control points if they were uploaded with them, which will have been passed into this component
            unitOfMeasure: null, // Georeferenced cads do not have unit of measure
            bounding,
            perimeter,
        };
        return config;
    };

    const nextStage = () => {
        const currentIndex = georeferencedModes.indexOf(mode);
        if (currentIndex === georeferencedModes.length - 1) {
            onFinish(getCadConfig());
            return;
        }

        const nextMode = georeferencedModes[currentIndex + 1];
        switch (mode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                mapboxCadGeoReferencerRef.current?.removeDrawings();
                break;
            default:
                break;
        }

        // pick the next stage based on the current mode using the enum order
        switch (nextMode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                mapboxCadGeoReferencerRef.current?.project(null, style);
                subjects.mode$.next(GeoReferencerCadMode.ClippingAndLayerExclusions);
                break;
            case GeoReferencerCadMode.LayerExclusions:
                mapboxCadGeoReferencerRef.current?.project(
                    clippedArea4326.coordinates[0].map((coord) => [coord[0], coord[1]]),
                    style
                );
                subjects.mode$.next(GeoReferencerCadMode.LayerExclusions);
                break;
            case GeoReferencerCadMode.Perimeter:
                subjects.mode$.next(GeoReferencerCadMode.Perimeter);
                break;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unexpected mode: ${GeoReferencerCadMode}`);
                break;
        }
    };

    const getTooltipText = () => {
        switch (mode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                if (!clippedArea) {
                    return "Draw clipped area to proceed";
                }
                break;
            case GeoReferencerCadMode.Perimeter:
                if (!perimeter) {
                    return "Draw perimeter to proceed";
                }
                return "Finish";
            default:
                break;
        }
        return "Next Stage";
    };

    const isNextStepDisabled = (): boolean => {
        switch (mode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                return !clippedArea;
            case GeoReferencerCadMode.Perimeter:
                return !perimeter;
            default:
                return false;
        }
    };

    // Poll config state until idle, then allow main effect to run
    useEffect(() => {
        let polling = true;
        let intervalId: NodeJS.Timeout;
        async function pollConfig() {
            try {
                const config = await cadApiContext.getConfig(); // Adjust URL as needed
                setFeaturesRead(config.featuresRead ?? 0); // Update features read count
                if (config.state === "idle" || config.state === "projecting") {
                    const geoJsonlayerNames = await cadApiContext.getGeoJsonLayers(); // Adjust URL as needed
                    setLayerNames(geoJsonlayerNames);
                    setConfigLoaded(true);
                    polling = false;
                    clearInterval(intervalId);
                }
            } catch (e) {
                // Optionally handle error
            }
        }
        pollConfig();
        intervalId = setInterval(() => {
            if (polling) pollConfig();
        }, 2000);
        return () => {
            polling = false;
            if (intervalId != null) clearInterval(intervalId);
        };
    }, []);

    // Main effect only runs after configLoaded is true
    useEffect(() => {
        if (!configLoaded) return;

        cadSourceRef.current = new CadSourceGeoReferenced(effectiveSourceEpsgCode);
        mapboxCadGeoReferencerRef.current = new MapboxCadGeoEditor(
            backgroundMapElementRef.current,
            () => {
                nextStage();
            },
            { getGoogleMapBackgroundApiKey, osMapsApiKey: OSKey },
            cadApiContext
        );

        const drawingSubscription = mapboxCadGeoReferencerRef.current.onDrawEnd().subscribe((drawnArea: Feature<Polygon>) => {
            const clippedArea = drawnArea.geometry;
            if (subjects.mode$.value === GeoReferencerCadMode.Perimeter) {
                setPerimeter(clippedArea);
            }
            if (subjects.mode$.value === GeoReferencerCadMode.ClippingAndLayerExclusions) {
                setClippedArea4326(clippedArea);
                cadSourceRef.current?.setClip(clippedArea);
                // if control points are provided, we must transform the drawn area back into the source coordinate system using control points
                if (controlPoints != null) {
                    const destPoints = controlPoints.map((coord) => [coord.source[0], coord.source[1]]);
                    const sourcePoints = controlPoints.map((coord) => [coord.target[0], coord.target[1]]);
                    // Compute the transform matrix
                    const transformer = new PerspectiveTransform(sourcePoints, destPoints);
                    const clippedCords = cadSourceRef.current?.clippedPolygonSource.coordinates[0].map((coord) => {
                        const [x, y] = transformer.map(coord[0], coord[1]);
                        return [x, y];
                    });
                    const clip = { ...bounding, coordinates: [clippedCords] };
                    setClippedArea(clip);
                } else {
                    setClippedArea(cadSourceRef.current?.clippedPolygonSource);
                }
                setBounding(bboxPolygon(bbox(drawnArea)).geometry);
            }
        });

        const modeSubscription = subjects.mode$.subscribe((newMode) => {
            setMode(newMode);
        });

        const layerIdsSubscription = subjects.filterLayers$.subscribe((layerIds) => {
            mapboxCadGeoReferencerRef.current?.filterCadLayers(layerIds);
            setExcludedLayerIds(layerIds);
        });

        if (backgroundMapElementRef.current == null) {
            throw new Error("Elements for attaching maps are null");
        }

        const backgroundSelectedSubscription = subjects.backgroundSelected$.subscribe((selectedMapBackground) => {
            mapboxCadGeoReferencerRef.current?.changeBackground(selectedMapBackground, style, subjects.filterLayers$.value);
        });

        mapboxCadGeoReferencerRef.current.onCadClicked().subscribe((clickedFeatures: GeoJSONFeature[]) => {
            if (subjects.mode$.value === GeoReferencerCadMode.LayerExclusions || subjects.mode$.value === GeoReferencerCadMode.ClippingAndLayerExclusions) {
                const layerIds = clickedFeatures.map((feature) => feature.properties?.eLayer).filter((layerId) => layerId != null);
                subjects.filterLayers$.next([...new Set((subjects.filterLayers$.value ?? []).concat(layerIds))]);
            }
        });

        const projectingSubscription = mapboxCadGeoReferencerRef.current.projecting$.subscribe((projecting) => {
            setProjecting(projecting);
        });

        const resetSubjectValues = () => {
            subjects.mode$.next(georeferencedModes[0]);
            subjects.filterLayers$.next([]);
        };

        // eslint-disable-next-line consistent-return
        return () => {
            mapboxCadGeoReferencerRef.current?.destroy();
            modeSubscription.unsubscribe();
            drawingSubscription.unsubscribe();
            layerIdsSubscription.unsubscribe();
            backgroundSelectedSubscription.unsubscribe();
            projectingSubscription.unsubscribe();
            resetSubjectValues();
        };
    }, [configLoaded]);

    return (
        <MapOptionsContainer>
            <StepperRow>
                <ModeStepperComponent mode={mode} georeferenced />
            </StepperRow>
            <ContentRow>
                <div className="sidebar">
                    <Header3>Map type</Header3>
                    <BackgroundSelectorComponent getMapBackgrounds={getMapBackgrounds} />
                    <Header3>Cad settings</Header3>
                    {(mode === GeoReferencerCadMode.ClippingAndLayerExclusions || mode === GeoReferencerCadMode.LayerExclusions) && <LayerListComponent layerNames={layerNames} />}
                </div>
                <div className="map-and-search">
                    {mode === GeoReferencerCadMode.ClippingAndLayerExclusions && <ClippingModeControls onDrawClicked={() => mapboxCadGeoReferencerRef.current?.startDraw()} />}
                    {mode === GeoReferencerCadMode.Perimeter && <ClippingModeControls onDrawClicked={() => mapboxCadGeoReferencerRef.current?.startDraw()} />}
                    {mode === GeoReferencerCadMode.WaitingForFile && <p>Reading CAD file... Read {featuresRead} features</p>}
                    <div className="map-container" style={{ display: mode === GeoReferencerCadMode.WaitingForFile ? "none" : "block" }}>
                        {projecting && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    right: 10,
                                    zIndex: 100,
                                    background: "#fffbe6",
                                    color: "#ad8b00",
                                    border: "1px solid #ffe58f",
                                    borderRadius: 4,
                                    padding: "8px 16px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    fontWeight: 500,
                                }}
                            >
                                Projecting CAD... Please wait
                            </div>
                        )}
                        <div className="map-element background-map" ref={backgroundMapElementRef} />
                        <NextStageButton>
                            <CursorTooltip text={getTooltipText()}>
                                <div>
                                    <SolidButton
                                        type="submit"
                                        variant="contained"
                                        style={{ height: "40px", minWidth: "140px", whiteSpace: "nowrap" }}
                                        disabled={isNextStepDisabled()}
                                        onClick={() => nextStage()}
                                    >
                                        {mode === georeferencedModes[georeferencedModes.length - 1] ? <span>Finish</span> : <span>Next Stage</span>}
                                    </SolidButton>
                                </div>
                            </CursorTooltip>
                        </NextStageButton>
                    </div>
                </div>
            </ContentRow>
        </MapOptionsContainer>
    );
}

const MapOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    .map-container {
        width: 100%;
        height: 100%;
        flex-grow: 1;
        position: relative;
    }

    .map-element {
        position: absolute;
        width: 100%;
        height: 100%;
    }

    .cad-map {
        z-index: 2;

        .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-logo {
            display: none !important;
        }

        .mapboxgl-ctrl-bottom-right {
            display: none !important;
        }
    }

    .background-map {
        z-index: 1;

        .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-logo {
            display: none !important;
        }

        .mapboxgl-ctrl-bottom-right {
            margin-right: 0;
        }
    }

    .map-and-search {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
    }

    .sidebar {
        display: flex;
        flex-direction: column;
        gap: 15px;

        box-sizing: border-box;
        z-index: 3;

        width: 300px;
        height: 100%;
        overflow-y: auto; /* Add scroll bar */

        ${muiInputFormsCSS}
    }
`;

const StepperRow = styled.div`
    width: 100%;
`;

const ContentRow = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;
    overflow: hidden;
`;

const NextStageButton = styled.div`
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 10;
`;
